import { useCallback, useMemo } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';

import ModalPortal from '../../../../../../../components/modal-portal';
import { useEnableLotTracking } from '../../../../../../../feature-flags/hooks/use-enable-lot-tracking';
import { useUtcFormattedDate } from '../../../../../../../hooks/use-datetime-formats';
import { messages } from './messages';
import styles from './styles.module.less';

import type { StockError } from '../../types';

type StockErrorsModalProps = {
  isLoading?: boolean;
  stockErrors: StockError[];
  onClose: () => void;
  onSaveAsDraft: () => void;
  onAdjustStock: (errors: StockError[]) => void;
};

type ExpirationDateCellProps = {
  expirationDate?: string;
};

const ExpirationDateCell = ({ expirationDate }: ExpirationDateCellProps) => {
  const formattedDate = useUtcFormattedDate(expirationDate ?? '');

  if (!expirationDate) {
    return null;
  }

  return <Text textType={TextType.Paragraph} value={formattedDate} />;
};

type HeaderCellProps = {
  message: MessageDescriptor;
};

const HeaderCell = ({ message }: HeaderCellProps) => (
  <th className={styles['table-header']}>
    <Text textType={TextType.Label} value={message} />
  </th>
);

type CellProps = {
  children: React.ReactNode;
  className?: string;
};

const Cell = ({ children, className }: CellProps) => (
  <td className={`${styles['table-cell']}${className ? ` ${className}` : ''}`}>{children}</td>
);

export const StockErrorsModal = ({ isLoading = false, stockErrors, onClose, onSaveAsDraft, onAdjustStock }: StockErrorsModalProps) => {
  const intl = useIntl();
  const isLotTrackingEnabled = useEnableLotTracking();
  const modalHeaderId = useMemo(() => GuidService.newGuid(), []);

  const handleAdjustStock = useCallback(() => {
    onAdjustStock(stockErrors);
  }, [onAdjustStock, stockErrors]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal className={styles['stock-errors-modal']} modalHeaderId={modalHeaderId} size='large'>
        <ModalHeader id={modalHeaderId}>
          <Text textType={TextType.H3} value={messages.modalHeader} />
        </ModalHeader>
        <ModalBody>
          <Text className={styles['modal-description']} textType={TextType.Paragraph} value={messages.modalDescription} />

          <div className={styles['table-container']}>
            <table className={styles['errors-table']}>
              <thead>
                <tr>
                  <HeaderCell message={messages.productColumnHeader} />
                  <HeaderCell message={messages.locationColumnHeader} />
                  {isLotTrackingEnabled ? (
                    <>
                      <HeaderCell message={messages.lotIdColumnHeader} />
                      <HeaderCell message={messages.expirationDateColumnHeader} />
                    </>
                  ) : null}
                  <HeaderCell message={messages.requestedColumnHeader} />
                  <HeaderCell message={messages.availableColumnHeader} />
                  <HeaderCell message={messages.missingColumnHeader} />
                </tr>
              </thead>
              <tbody>
                {stockErrors.map((error) => (
                  <tr key={`${error.productId}-${error.locationId}-${error.lotUuid ?? ''}`} className={styles['table-row']}>
                    <Cell>
                      <Text textType={TextType.Paragraph} value={error.name} />
                      <div className={styles['product-sku']}>
                        <Text formattedMessageValues={{ sku: error.sku }} textType={TextType.Caption} value={messages.productSku} />
                      </div>
                    </Cell>
                    <Cell>
                      <Text textType={TextType.Paragraph} value={error.locationName} />
                    </Cell>
                    {isLotTrackingEnabled ? (
                      <>
                        <Cell>
                          <Text textType={TextType.Paragraph} value={error.lotId ?? ''} />
                        </Cell>
                        <Cell>
                          <ExpirationDateCell expirationDate={error.expirationDate} />
                        </Cell>
                      </>
                    ) : null}
                    <Cell>
                      <Text textType={TextType.Paragraph} value={String(error.requestedQuantity)} />
                    </Cell>
                    <Cell>
                      <Text textType={TextType.Paragraph} value={String(error.availableQuantity)} />
                    </Cell>
                    <Cell className={styles['missing-quantity']}>
                      <Text textType={TextType.Paragraph} value={String(error.missingQuantity)} />
                    </Cell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModalBody>
        <ModalFooter>
          <FlexRow className={styles['modal-footer-buttons']} gap={Gap.Space8}>
            <Button disabled={isLoading} onClick={onClose} theme={THEME_CONSTANTS.white}>
              <Text textType={TextType.Caption} value={messages.closeButton} />
            </Button>
            <Tooltip text={intl.formatMessage(messages.saveAsDraftTooltip)}>
              <Button disabled={isLoading} isLoading={isLoading} onClick={onSaveAsDraft} theme={THEME_CONSTANTS.secondary}>
                <Text textType={TextType.Caption} value={messages.saveAsDraftButton} />
              </Button>
            </Tooltip>
            <Tooltip text={intl.formatMessage(messages.adjustStockTooltip)}>
              <Button disabled={isLoading} isLoading={isLoading} onClick={handleAdjustStock} theme={THEME_CONSTANTS.primary}>
                <Text textType={TextType.Caption} value={messages.adjustStockButton} />
              </Button>
            </Tooltip>
          </FlexRow>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
